import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp, Clock, Monitor, Target,
  Award, AlertTriangle, CheckCircle2, Lightbulb,
  Calendar, ArrowRight, Download, Loader2, FileText
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { generatePDF } from "@/services/diagnosticService";

/**
 * Real schema mapping:
 *
 * diagnostic_scores (single row):
 *   finance_score, time_score, digital_score, strategy_score, global_score,
 *   maturity_level, priority_1, priority_2, priority_3, score_details
 *
 * ai_reports (direct columns, NOT a JSON blob):
 *   summary, strengths, weaknesses, recommendations,
 *   action_plan_30_days, action_plan_90_days, recommended_services
 */

const scoreFields = [
  { key: "finance_score", label: "Finance", icon: TrendingUp },
  { key: "time_score", label: "Temps dirigeant", icon: Clock },
  { key: "digital_score", label: "Digitalisation", icon: Monitor },
  { key: "strategy_score", label: "Stratégie", icon: Target },
];

function ScoreGauge({ score, label, icon: Icon }) {
  const safeScore = typeof score === "number" ? score : 0;
  const percent = Math.round((safeScore / 10) * 100);
  const color = percent >= 70 ? "#59C3A6" : percent >= 40 ? "#F59E0B" : "#EF4444";

  return (
    <div className="p-5 rounded-2xl bg-[#112A4F]/30 border border-white/5">
      <div className="flex items-center gap-3 mb-3">
        {Icon && <Icon size={18} className="text-[#59C3A6]" />}
        <span className="text-sm font-medium text-slate-300">{label}</span>
      </div>
      <div className="flex items-end gap-2 mb-2">
        <span className="font-heading text-3xl font-bold" style={{ color }}>{safeScore.toFixed(1)}</span>
        <span className="text-slate-500 text-sm mb-1">/ 10</span>
      </div>
      <div className="w-full h-2 bg-[#0B1F3B] rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
      </div>
    </div>
  );
}

function MaturityBadge({ level }) {
  const levels = {
    debutant: { label: "Débutant", color: "text-red-400 bg-red-400/10 border-red-400/20" },
    intermediaire: { label: "Intermédiaire", color: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20" },
    avance: { label: "Avancé", color: "text-[#59C3A6] bg-[#59C3A6]/10 border-[#59C3A6]/20" },
    expert: { label: "Expert", color: "text-blue-400 bg-blue-400/10 border-blue-400/20" },
  };
  const cfg = levels[level] || { label: level, color: "text-slate-400 bg-slate-400/10 border-slate-400/20" };

  return (
    <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-semibold ${cfg.color}`}>
      <Award size={14} /> {cfg.label}
    </span>
  );
}

// Safely parse a field that could be JSON string or already an array/object
function safeParse(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch { return value; }
}

// Render a list item, handling both string and object items
function renderItem(item) {
  if (typeof item === "string") return item;
  if (typeof item === "object" && item !== null) {
    return item.description || item.title || item.text || item.label || JSON.stringify(item);
  }
  return String(item);
}

export default function DiagnosticResults({ scores, report, sessionId }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const pdfButtonRef = useRef(null);
  
  // scores = single row from diagnostic_scores
  // report = single row from ai_reports (direct columns)

  const priorities = [scores?.priority_1, scores?.priority_2, scores?.priority_3].filter(Boolean);
  const strengths = safeParse(report?.strengths);
  const weaknesses = safeParse(report?.weaknesses);
  const recommendations = safeParse(report?.recommendations);
  const actionPlan30 = safeParse(report?.action_plan_30_days);
  const actionPlan90 = safeParse(report?.action_plan_90_days);
  const recommendedServices = safeParse(report?.recommended_services);

  // Scroll to PDF button after generation
  useEffect(() => {
    if (pdfGenerated && pdfButtonRef.current) {
      pdfButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [pdfGenerated]);

  // Handle PDF generation and download
  const handleDownloadPDF = async () => {
    // Prevent double clicks
    if (pdfLoading || !sessionId) {
      if (!sessionId) toast.error("Session non disponible");
      return;
    }

    setPdfLoading(true);
    console.log("=== PDF DOWNLOAD INITIATED ===");
    console.log("Session ID:", sessionId);
    
    try {
      const data = await generatePDF(sessionId);
      console.log("PDF generation response:", data);

      // Succès - afficher le message approprié
      setPdfGenerated(true);
      
      if (data.download_url) {
        // Cas idéal : on a l'URL, on ouvre le PDF
        window.open(data.download_url, "_blank");
        setPdfUrl(data.download_url);
        
        toast.success("Votre rapport a été généré avec succès !", {
          description: "Le PDF s'ouvre dans un nouvel onglet",
          duration: 5000,
        });
      } else {
        // Cas où le PDF a été généré mais pas d'URL (timeout mais succès)
        toast.success("Votre rapport a été envoyé par email !", {
          description: "Vérifiez votre boîte de réception",
          duration: 5000,
        });
      }
      
      // Email notification toast
      if (data.email_status === "sent" || data.email_status === "unknown") {
        setTimeout(() => {
          toast.success("Un email avec le rapport vous a été envoyé", {
            icon: "📧",
            duration: 4000,
          });
        }, 1500);
      } else if (data.email_status === "partial") {
        setTimeout(() => {
          toast.info("L'email a été partiellement envoyé", { duration: 3000 });
        }, 1500);
      }
    } catch (err) {
      console.error("=== PDF GENERATION ERROR ===");
      console.error("Error:", err?.message);
      
      // Afficher l'erreur seulement si c'est une vraie erreur
      const errorMsg = err?.message || "Erreur inconnue";
      toast.error("Erreur lors de la génération du rapport", {
        description: errorMsg.length > 100 ? errorMsg.substring(0, 100) + "..." : errorMsg,
        duration: 8000,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Re-download PDF if already generated
  const handleRedownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
      toast.success("Ouverture du PDF...");
    }
  };

  return (
    <div data-testid="diagnostic-results" className="space-y-8">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-20 h-20 rounded-full bg-[#59C3A6]/20 flex items-center justify-center mx-auto mb-5"
        >
          <CheckCircle2 size={40} className="text-[#59C3A6]" />
        </motion.div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
          Votre diagnostic est prêt
        </h2>
        {scores?.maturity_level && (
          <div className="mb-4">
            <MaturityBadge level={scores.maturity_level} />
          </div>
        )}
      </div>

      {/* CTA Expert - TOP (before scores) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-center"
      >
        <Link to="/contact">
          <Button
            data-testid="results-cta-contact-top"
            className="bg-[#0B1F3B] border-2 border-[#59C3A6] text-white hover:bg-[#112A4F] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105"
          >
            Discuter de mes résultats avec un expert
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </motion.div>

      {/* Global Score */}
      {scores?.global_score != null && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-8 rounded-2xl bg-[#112A4F]/40 border border-[#59C3A6]/15 text-center"
        >
          <span className="text-slate-400 text-sm uppercase tracking-wider">Score global</span>
          <div className="flex items-end justify-center gap-2 mt-2">
            <span className="font-heading text-6xl font-bold text-[#59C3A6]">
              {typeof scores.global_score === "number" ? scores.global_score.toFixed(1) : "—"}
            </span>
            <span className="text-slate-500 text-xl mb-2">/ 10</span>
          </div>
        </motion.div>
      )}

      {/* Category Scores (from individual columns) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {scoreFields.map((field, i) => {
          const val = scores?.[field.key];
          if (val == null) return null;
          return (
            <motion.div
              key={field.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <ScoreGauge score={val} label={field.label} icon={field.icon} />
            </motion.div>
          );
        })}
      </div>

      {/* Summary from ai_reports.summary */}
      {report?.summary && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5"
        >
          <h3 className="font-heading text-xl font-semibold text-white mb-3 flex items-center gap-2">
            <Lightbulb size={20} className="text-[#59C3A6]" /> Résumé
          </h3>
          <p className="text-slate-300 text-base leading-relaxed">
            {typeof report.summary === "string" ? report.summary : JSON.stringify(report.summary)}
          </p>
        </motion.div>
      )}

      {/* Priorities from diagnostic_scores.priority_1/2/3 */}
      {priorities.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5"
        >
          <h3 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Target size={20} className="text-[#59C3A6]" /> Priorités
          </h3>
          <ul className="space-y-2">
            {priorities.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-300 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#59C3A6]/10 text-[#59C3A6] flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                {renderItem(p)}
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* Strengths & Weaknesses from ai_reports columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {strengths && (Array.isArray(strengths) ? strengths.length > 0 : true) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="p-6 rounded-2xl bg-[#59C3A6]/5 border border-[#59C3A6]/15"
          >
            <h3 className="font-heading text-lg font-semibold text-[#59C3A6] mb-3 flex items-center gap-2">
              <CheckCircle2 size={18} /> Forces
            </h3>
            <ul className="space-y-2">
              {(Array.isArray(strengths) ? strengths : [strengths]).map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-[#59C3A6] mt-0.5">+</span> {renderItem(s)}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {weaknesses && (Array.isArray(weaknesses) ? weaknesses.length > 0 : true) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="p-6 rounded-2xl bg-red-500/5 border border-red-500/15"
          >
            <h3 className="font-heading text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
              <AlertTriangle size={18} /> Faiblesses
            </h3>
            <ul className="space-y-2">
              {(Array.isArray(weaknesses) ? weaknesses : [weaknesses]).map((w, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-red-400 mt-0.5">-</span> {renderItem(w)}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </div>

      {/* Recommendations from ai_reports.recommendations */}
      {recommendations && (Array.isArray(recommendations) ? recommendations.length > 0 : true) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5"
        >
          <h3 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Lightbulb size={20} className="text-[#59C3A6]" /> Recommandations
          </h3>
          <ul className="space-y-3">
            {(Array.isArray(recommendations) ? recommendations : [recommendations]).map((r, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#0B1F3B]/40">
                <ArrowRight size={16} className="text-[#59C3A6] mt-0.5 shrink-0" />
                <span className="text-slate-300 text-sm">{renderItem(r)}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* Action Plans from ai_reports columns */}
      {(actionPlan30 || actionPlan90) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {actionPlan30 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
              className="p-6 rounded-2xl bg-[#112A4F]/30 border border-[#59C3A6]/10"
            >
              <h3 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-[#59C3A6]" /> Plan 30 jours
              </h3>
              <ul className="space-y-2">
                {(Array.isArray(actionPlan30) ? actionPlan30 : [actionPlan30]).map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                    <span className="text-[#59C3A6] font-bold">·</span> {renderItem(a)}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {actionPlan90 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              className="p-6 rounded-2xl bg-[#112A4F]/30 border border-[#59C3A6]/10"
            >
              <h3 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-[#59C3A6]" /> Plan 90 jours
              </h3>
              <ul className="space-y-2">
                {(Array.isArray(actionPlan90) ? actionPlan90 : [actionPlan90]).map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                    <span className="text-[#59C3A6] font-bold">·</span> {renderItem(a)}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>
      )}

      {/* Recommended services */}
      {recommendedServices && (Array.isArray(recommendedServices) ? recommendedServices.length > 0 : true) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
          className="p-6 rounded-2xl bg-[#112A4F]/30 border border-[#59C3A6]/15"
        >
          <h3 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Award size={18} className="text-[#59C3A6]" /> Services recommandés
          </h3>
          <ul className="space-y-2">
            {(Array.isArray(recommendedServices) ? recommendedServices : [recommendedServices]).map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle2 size={14} className="text-[#59C3A6] mt-0.5 shrink-0" /> {renderItem(s)}
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* CTA Section */}
      <motion.div 
        ref={pdfButtonRef}
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 1.1 }}
        className="pt-6"
      >
        {/* PDF Download Card */}
        <div className="p-6 rounded-2xl bg-gradient-to-r from-[#112A4F]/50 to-[#0B1F3B]/50 border border-[#59C3A6]/20 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center">
                <FileText size={24} className="text-[#59C3A6]" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Rapport PDF complet</h4>
                <p className="text-slate-400 text-sm">
                  {pdfGenerated 
                    ? "Votre rapport est prêt !" 
                    : "Téléchargez votre analyse détaillée"}
                </p>
              </div>
            </div>
            
            {pdfGenerated ? (
              <div className="flex items-center gap-3">
                <span className="text-[#59C3A6] text-sm flex items-center gap-1">
                  <CheckCircle2 size={16} /> Généré
                </span>
                <Button
                  data-testid="results-redownload-pdf"
                  onClick={handleRedownload}
                  variant="outline"
                  className="border-[#59C3A6] text-[#59C3A6] hover:bg-[#59C3A6]/10 font-semibold px-6 py-2.5 h-auto rounded-full"
                >
                  <Download className="mr-2" size={16} />
                  Recevoir à nouveau mon rapport
                </Button>
              </div>
            ) : (
              <Button
                data-testid="results-download-pdf"
                onClick={handleDownloadPDF}
                disabled={pdfLoading}
                className={`font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 ${
                  pdfLoading 
                    ? "bg-slate-700 text-slate-300 cursor-wait" 
                    : "bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] hover:scale-105 shadow-[0_0_20px_rgba(89,195,166,0.25)]"
                }`}
              >
                {pdfLoading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <Download className="mr-2" size={18} />
                    Recevoir mon rapport
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <Link to="/contact">
            <Button
              data-testid="results-cta-contact"
              className="bg-[#0B1F3B] border-2 border-[#59C3A6] text-white hover:bg-[#112A4F] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105"
            >
              Discuter de mes résultats avec un expert
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
